import type { RegisteredComponent } from "@builder.io/sdk-react/edge";
import { register } from "@builder.io/sdk-react/edge";

import { FeatureSection } from "./ui/feature-section";
import { Heading } from "./ui/heading";
import { Prose } from "./ui/prose";
import { Button } from "./ui/button";
import { Centered } from "./helpers/centered";
import { PricingCard } from "./ui/pricing-card";
import { MembershipPlan } from "~/lib/schemas";
import { BuilderCard } from "./helpers/builder-card";
import { Container } from "./helpers/container";
import { Columns } from "./helpers/columns";
import { CTASection } from "./ui/cta-section";
import { FeatureList } from "./feature-list";
import { AuroraBackground } from "./ui/aurora-background";
import { MembershipPlanDetails } from "./membership-plan-details";
import { SavingsComparisonTable } from "./savings-comparison-table";
import { ImageWithCaption } from "./ui/image-with-caption";
import { SessionLifetimeDealModal } from "./session-lifetime-deal-modal";
import { Testimonials } from "./ui/testimonials";
import { BuilderLink } from "./helpers/builder-link";
import { CcoCarousel } from "./cco-carousel";
import { CCOList } from "./cco-list";
import { CountdownBar } from "./ui/countdown-bar";
import { TagList } from "./ui/tag-list";
import { LabelList } from "./ui/label-list";
import { AuthorGrid } from "./ui/author-grid";
import { TimeZoneOverview } from "./ui/timezone-overview";
import { FullScreenCTA } from "./ui/full-screen-cta";
import { DailySummitAgenda } from "./ui/daily-summit-agenda";
import { SummitRegistration } from "./summit/summit-registration";
import { ICON_NAMES } from "~/lib/icon-names";

export const configureBuilder = () => {
  if ("undefined" === typeof window || "undefined" !== typeof parent) {
    return;
  }

  const baseSize = {
    xs: "20rem",
    sm: "24rem",
    md: "28rem",
    lg: "32rem",
    xl: "36rem",
    "2xl": "42rem",
    "3xl": "48rem",
    "4xl": "56rem",
    "5xl": "64rem",
    "6xl": "72rem",
    "7xl": "80rem",
    prose: "65ch",
  };

  const borderRadius = {
    DEFAULT: "0.25rem",
    none: "0",
    sm: "0.125rem",
    md: "0.375rem",
    lg: "0.5rem",
    xl: "0.75rem",
    "2xl": "1rem",
    "3xl": "1.5rem",
    full: "9999px",
  };

  const boxShadow = {
    DEFAULT: [
      "var(--tw-inset-shadow), 0 1px 3px 0 rgb(0 0 0 / 0.1)",
      "var(--tw-inset-shadow), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    ],
    none: "0 0 rgb(0 0 0 / 0)",
    sm: "var(--tw-inset-shadow), 0 1px 2px 0 rgb(0 0 0 / 0.05)",
    md: [
      "var(--tw-inset-shadow), 0 4px 6px -1px rgb(0 0 0 / 0.1)",
      "var(--tw-inset-shadow), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    ],
    lg: [
      "var(--tw-inset-shadow), 0 10px 15px -3px rgb(0 0 0 / 0.1)",
      "var(--tw-inset-shadow), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    ],
    xl: [
      "var(--tw-inset-shadow), 0 20px 25px -5px rgb(0 0 0 / 0.1)",
      "var(--tw-inset-shadow), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    ],
    "2xl": "var(--tw-inset-shadow), 0 25px 50px -12px rgb(0 0 0 / 0.25)",
    inner: "inset 0 2px 4px 0 rgb(0 0 0 / 0.05)",
  };

  register("editor.settings", {
    hideHeatMap: true,
    hideFormComponents: true,
    hideDataTab: false,
    hideABTab: true,
    hidePageUrlEditor: false,
    hideTemplates: false,

    hideStyleTab: false,
    styleStrictMode: true,
    designTokens: {
      colors: ["brand", "violet", "orange", "gray"].flatMap((color) =>
        [
          "50",
          "100",
          "200",
          "300",
          "400",
          "500",
          "600",
          "700",
          "800",
          "900",
          "950",
        ].map((brightness) => ({
          name: `${color}-${brightness}`,
          value: `var(--${color}-${brightness})`,
        })),
      ),
      spacing: [
        ...[2, 4, 8, 16, 24, 32, 48, 64, 96, 128].map((size) => ({
          name: `${size}`,
          value: `calc(var(--spacing) * ${size})`,
        })),
        ...Object.entries(baseSize).map(([name, value]) => ({
          name,
          value: value,
        })),
      ],
      fontFamily: [
        { name: "Heading", value: "var(--font-heading, sans-serif)" },
        { name: "Primary", value: "var(--font-sans, sans-serif)" },
      ],
      borderRadius: Object.entries(borderRadius).map(([name, value]) => ({
        name,
        value: value,
      })),
      boxShadow: Object.entries(boxShadow).map(([name, value]) => ({
        name,
        value: (Array.isArray(value) ? value : [value]).join(", "),
      })),
    },
  });
};

/**
 * This array is used to integrate custom components within Builder.
 * https://www.builder.io/c/docs/custom-components-intro
 *
 * These components will be found the "Custom Components"
 * section of Builder's visual editor.
 * You can also turn on "components only mode" to limit
 * editing to only these components.
 * https://www.builder.io/c/docs/guides/components-only-mode
 */
export const CUSTOM_COMPONENTS: RegisteredComponent[] = [
  {
    component: Container,
    name: "Container",
    canHaveChildren: true,
    inputs: [
      {
        name: "pagePadding",
        type: "boolean",
        required: false,
      },
      {
        name: "maxWidth",
        type: "string",
        enum: ["prose", "sm", "md", "lg", "xl", "2xl", "full"],
        required: false,
      },
    ],
  },
  {
    component: Columns,
    name: "Distributed Columns",
    canHaveChildren: true,
    inputs: [
      {
        name: "count",
        type: "string",
        enum: ["1", "2", "3", "4"],
        required: false,
      },
    ],
  },
  {
    component: FeatureList,
    name: "FeatureList",
    friendlyName: "Feature List",
    inputs: [
      {
        name: "features",
        type: "list",
        subFields: [{ name: "feature", type: "string", required: true }],
        required: true,
      },
    ],
  },
  {
    component: PricingCard,
    name: "PricingCard",
    canHaveChildren: true,
    inputs: [
      {
        name: "membershipPlan",
        type: "string",
        enum: Object.values(MembershipPlan),
        required: false,
      },
      { name: "price", type: "string", required: false },
      { name: "priceSuffix", type: "string", required: false },
      { name: "description", type: "string", required: false },
      { name: "highlighted", type: "boolean", defaultValue: false },
    ],
  },
  {
    component: Prose,
    name: "Prose",
    inputs: [
      {
        name: "content",
        type: "richText",
        defaultValue: "<p>This is some content</p>",
        required: true,
      },
    ],
  },
  {
    component: BuilderCard,
    name: "Card",
    canHaveChildren: true,
    inputs: [
      {
        name: "title",
        type: "string",
        required: false,
      },
      {
        name: "description",
        type: "string",
        required: false,
      },
      {
        name: "fillHeight",
        type: "boolean",
        required: false,
      },
    ],
  },
  {
    component: CTASection,
    name: "CTA Section",
    inputs: [
      {
        name: "title",
        type: "string",
        required: false,
      },
      {
        name: "description",
        type: "string",
        required: false,
      },
      {
        name: "link",
        type: "string",
        required: false,
      },
      {
        name: "linkText",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: BuilderLink,
    name: "Link",
    canHaveChildren: true,
    inputs: [
      {
        name: "href",
        type: "string",
        required: true,
      },
      {
        name: "target",
        type: "string",
        enum: ["_self", "_blank", "_parent", "_top"],
      },
    ],
  },
  {
    component: Button,
    name: "DCMButton",
    canHaveChildren: true,
    inputs: [
      {
        name: "variant",
        type: "string",
        defaultValue: "default",
        enum: ["default", "secondary", "fancy", "outline", "ghost"],
        required: true,
      },
      {
        name: "size",
        type: "string",
        defaultValue: "default",
        enum: ["default", "sm", "lg"],
        required: true,
      },
    ],
  },
  {
    component: FeatureSection,
    name: "FeatureSection",
    inputs: [
      {
        name: "layout",
        type: "string",
        enum: ["left", "right"],
        required: true,
      },
      {
        name: "look",
        type: "string",
        enum: ["active", "inactive"],
        required: true,
      },
      {
        name: "preHeadline",
        type: "string",
        required: true,
      },
      {
        name: "headline",
        type: "string",
        required: true,
      },
      {
        name: "description",
        type: "string",
        required: true,
      },
    ],
  },
  {
    component: MembershipPlanDetails,
    name: "MembershipPlanDetails",
    friendlyName: "Plan Details Table (expandable)",
  },
  {
    component: Heading,
    name: "Heading",
    inputs: [
      {
        name: "className",
        type: "string",
      },
      {
        name: "preHeadline",
        type: "string",
      },
      {
        name: "headline",
        type: "string",
      },
      {
        name: "subheadline",
        type: "string",
      },
      {
        name: "isH1",
        type: "boolean",
        friendlyName: "Top-level heading? (H1)",
        defaultValue: false,
      },
    ],
  },
  {
    component: Centered,
    name: "Centered",
    canHaveChildren: true,
  },
  {
    component: AuroraBackground,
    name: "AuroraBackground",
    friendlyName: "Aurora Background",
    canHaveChildren: true,
  },
  {
    component: SavingsComparisonTable,
    name: "SavingsComparisonTable",
    description:
      "A table comparing software costs and savings across different membership plans",
    inputs: [
      {
        name: "className",
        type: "string",
      },
    ],
  },
  {
    component: ImageWithCaption,
    name: "ImageWithCaption",
    friendlyName: "Image with Caption",
    canHaveChildren: true,
    inputs: [
      {
        name: "caption",
        type: "string",
        required: true,
      },
    ],
  },
  {
    component: SessionLifetimeDealModal,
    name: "Session Lifetime Deal Modal",
    friendlyName: "Session Lifetime Deal Modal",
  },
  {
    component: Testimonials,
    name: "Testimonials",
    friendlyName: "Testimonials",
    inputs: [
      {
        name: "testimonials",
        type: "list",
        subFields: [
          {
            name: "author",
            type: "string",
            required: true,
          },
          {
            name: "role",
            type: "string",
            required: false,
          },
          {
            name: "title",
            type: "string",
            required: false,
          },
          {
            name: "content",
            type: "richText",
            required: true,
          },
          {
            name: "setHTML",
            type: "boolean",
            defaultValue: true,
            hideFromUI: false,
          },
        ],
      },
    ],
  },
  {
    component: CcoCarousel,
    name: "CcoCarousel",
    friendlyName: "CCO Carousel",
    canHaveChildren: false,
  },
  {
    component: CCOList,
    name: "CCOList",
    friendlyName: "CCO List",
    canHaveChildren: false,
  },
  {
    component: CountdownBar,
    name: "CountdownBar",
    friendlyName: "Countdown Bar",
    canHaveChildren: false,
    noWrap: true,
    inputs: [
      {
        name: "text",
        type: "string",
        required: true,
        defaultValue: "Summit starts in:",
      },
      {
        name: "targetDate",
        type: "date",
        required: true,
      },
      {
        name: "ctaText",
        type: "string",
        required: true,
        defaultValue: "Register for free",
      },
      {
        name: "ctaLink",
        type: "string",
        required: true,
        defaultValue: "/register",
      },
      {
        name: "showCountdown",
        type: "boolean",
        required: false,
        defaultValue: true,
      },
      {
        name: "ctaVariant",
        type: "string",
        enum: ["default", "secondary", "fancy", "outline", "ghost"],
        required: false,
        defaultValue: "fancy",
      },
      {
        name: "ctaSize",
        type: "string",
        enum: ["default", "sm", "lg"],
        required: false,
        defaultValue: "default",
      },
    ],
  },
  {
    component: TagList,
    name: "TagList",
    friendlyName: "Tag List",
    canHaveChildren: false,
    inputs: [
      {
        name: "tags",
        type: "list",
        subFields: [
          { name: "id", type: "string", required: true },
          { name: "title", type: "string", required: true },
          {
            name: "property",
            type: "string",
            required: true,
          },
          {
            name: "icon",
            type: "string",
            required: false,
            enum: [...ICON_NAMES],
          },
        ],
        required: true,
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: LabelList,
    name: "LabelList",
    friendlyName: "Label List",
    canHaveChildren: false,
    inputs: [
      {
        name: "labels",
        type: "list",
        subFields: [
          { name: "id", type: "string", required: true },
          { name: "text", type: "string", required: true },
          {
            name: "variant",
            type: "string",
            enum: ["default", "secondary", "outline"],
            required: false,
          },
          {
            name: "size",
            type: "string",
            enum: ["sm", "md", "lg", "xl"],
            required: false,
          },
        ],
        required: true,
      },
      {
        name: "gap",
        type: "string",
        enum: ["sm", "md", "lg"],
        required: false,
        defaultValue: "md",
      },
      {
        name: "wrap",
        type: "boolean",
        required: false,
        defaultValue: true,
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: AuthorGrid,
    name: "AuthorGrid",
    friendlyName: "Author Grid",
    canHaveChildren: false,
    inputs: [
      {
        name: "authors",
        type: "list",
        subFields: [
          { name: "id", type: "string", required: true },
          { name: "name", type: "string", required: true },
          { name: "image", type: "file", required: true },
          { name: "title", type: "string", required: false },
          { name: "subtitle", type: "string", required: false },
          { name: "mainText", type: "richText", required: false },
        ],
        required: true,
      },
      {
        name: "columns",
        type: "string",
        enum: ["1", "2", "3", "4"],
        defaultValue: "3",
        required: false,
      },
      {
        name: "gap",
        type: "string",
        enum: ["sm", "md", "lg"],
        defaultValue: "md",
        required: false,
      },
      {
        name: "imageSize",
        type: "string",
        enum: ["sm", "md", "lg"],
        defaultValue: "md",
        required: false,
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: TimeZoneOverview,
    name: "TimeZoneOverview",
    friendlyName: "Time Zone Overview",
    canHaveChildren: false,
    inputs: [
      {
        name: "title",
        type: "string",
        defaultValue: "Attend free via Zoom",
        required: false,
      },
      {
        name: "dateRange",
        type: "string",
        defaultValue: "Friday, 25th - Sunday, 27th July 2025",
        required: false,
      },
      {
        name: "timeZones",
        type: "list",
        subFields: [
          {
            name: "flagImage",
            type: "string",
            required: true,
            defaultValue: "us.svg",
          },
          { name: "timeRange", type: "string", required: true },
          { name: "city", type: "string", required: true },
        ],
        required: true,
        defaultValue: [
          {
            flagImage: "us.svg",
            timeRange: "8am - 12pm",
            city: "Los Angeles",
          },
          {
            flagImage: "us.svg",
            timeRange: "11am - 3pm",
            city: "New York",
          },
          {
            flagImage: "gb.svg",
            timeRange: "4pm - 8pm",
            city: "London",
          },
          {
            flagImage: "ae.svg",
            timeRange: "7pm - 11pm",
            city: "Dubai",
          },
          {
            flagImage: "in.svg",
            timeRange: "8:30pm - 12:30am (+1)",
            city: "Delhi",
          },
        ],
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: FullScreenCTA,
    name: "FullScreenCTA",
    friendlyName: "Full Screen CTA",
    canHaveChildren: true,
    inputs: [
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
    defaultChildren: [
      {
        "@type": "@builder.io/sdk:Element",
        "@version": 2,
        id: "cta-heading",
        component: {
          name: "Heading",
          options: {
            preHeadline: "Don't Miss Out",
            headline: "Ready to Transform Your Business?",
            subheadline:
              "Join thousands of entrepreneurs who have accelerated their growth with our proven strategies.",
          },
        },
      },
      {
        "@type": "@builder.io/sdk:Element",
        "@version": 2,
        id: "cta-button",
        component: {
          name: "DCMButton",
          options: {
            variant: "fancy",
            size: "lg",
          },
        },
        children: [
          {
            "@type": "@builder.io/sdk:Element",
            "@version": 2,
            component: {
              name: "Text",
              options: {
                text: "Get Started Today",
              },
            },
          },
        ],
      },
    ],
  },
  {
    component: DailySummitAgenda,
    name: "DailySummitAgenda",
    friendlyName: "Daily Summit Agenda",
    canHaveChildren: false,
    inputs: [
      {
        name: "title",
        type: "string",
        defaultValue: "Summit Agenda",
        required: false,
      },
      {
        name: "showTimes",
        type: "boolean",
        defaultValue: false,
        required: false,
      },
      {
        name: "description",
        type: "string",
        required: false,
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: SummitRegistration,
    name: "SummitRegistration",
    friendlyName: "Summit Registration",
    canHaveChildren: false,
    inputs: [
      {
        name: "redirectUrl",
        type: "string",
        friendlyName: "Redirect URL (after registration)",
        required: false,
        helperText:
          "URL to redirect users after successful registration. Leave empty for confirmation message.",
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
];
